import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  Measurement,
  SanctionedPlan,
  ScanJob,
  SbcApiService,
  ViolationFlag,
} from '../services/sbc-api.service';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { SessionService } from '../../../core/services/session.service';

type SummaryCard = {
  key: string;
  label: string;
  value: number;
  sub: string;
  route: string;
  show: boolean;
};

type ActiveBuildingCheck = {
  plotRef: string;
  progressLabel: string;
  ruleCheckLabel: string;
  ruleCheckStatus: string;
  violationLabel: string;
  violationStatus: string | null;
  planId?: number;
  flagId?: number;
  measurementPlotRef?: string;
};

type QuickAction = {
  label: string;
  detail: string;
  route: string;
  show: boolean;
};

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterLink, PageRefreshButtonComponent],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.css',
})
export class DashboardComponent {
  private readonly api = inject(SbcApiService);
  readonly router = inject(Router);
  readonly session = inject(SessionService);

  loading = signal(true);
  error = signal('');
  quickActionsOpen = signal(false);
  plans = signal<SanctionedPlan[]>([]);
  scans = signal<ScanJob[]>([]);
  measurements = signal<Measurement[]>([]);
  flags = signal<ViolationFlag[]>([]);

  readonly summaryCards = computed((): SummaryCard[] => {
    const openFlags = this.flags().filter((f) => f.statusCd === 'open').length;
    const readyScans = this.scans().filter((s) => s.statusCd === 'ready').length;
    const finalMeasurements = this.measurements().filter((m) => m.statusCd === 'final').length;

    return [
      {
        key: 'scans',
        label: 'LiDAR scans',
        value: this.scans().length,
        sub: `${readyScans} ready for processing`,
        route: '/enforcement/lidar-survey',
        show: this.session.canAccessScans(),
      },
      {
        key: 'measurements',
        label: 'Measurements',
        value: this.measurements().length,
        sub: `${finalMeasurements} finalised`,
        route: '/enforcement/measurements',
        show: this.session.canAccessMeasurements(),
      },
      {
        key: 'rule-checks',
        label: 'Rule checks',
        value: this.flags().length,
        sub: 'Total compliance evaluations',
        route: '/enforcement/compliance-runs',
        show: this.session.canAccessMeasurements() || this.session.canAccessFlags(),
      },
      {
        key: 'violations',
        label: 'Open violations',
        value: openFlags,
        sub: openFlags ? 'Requires officer review' : 'No open cases',
        route: '/enforcement/flags',
        show: this.session.canAccessFlags(),
      },
      {
        key: 'plans',
        label: 'Sanctioned plans',
        value: this.plans().length,
        sub: `${this.plans().filter((p) => p.statusCd === 'verified').length} verified`,
        route: '/enforcement/plans',
        show: this.session.canAccessPlans(),
      },
    ].filter((c) => c.show);
  });

  readonly complianceSummary = computed(() => {
    const plans = this.plans();
    const scans = this.scans();
    const measurements = this.measurements();
    const flags = this.flags();

    return [
      {
        label: 'Plans verified',
        value: plans.filter((p) => p.statusCd === 'verified').length,
        total: plans.length,
        show: this.session.canAccessPlans(),
      },
      {
        label: 'Scans processed',
        value: scans.filter((s) => s.statusCd === 'ready' || s.statusCd === 'processed').length,
        total: scans.length,
        show: this.session.canAccessScans(),
      },
      {
        label: 'Measurements final',
        value: measurements.filter((m) => m.statusCd === 'final').length,
        total: measurements.length,
        show: this.session.canAccessMeasurements(),
      },
      {
        label: 'Flags under review',
        value: flags.filter((f) => f.statusCd === 'under_review' || f.statusCd === 'open').length,
        total: flags.length,
        show: this.session.canAccessFlags(),
      },
      {
        label: 'Flags resolved',
        value: flags.filter((f) => f.statusCd === 'accepted' || f.statusCd === 'rejected').length,
        total: flags.length,
        show: this.session.canAccessFlags(),
      },
    ].filter((item) => item.show);
  });

  readonly quickActions = computed((): QuickAction[] =>
    [
      {
        label: 'Upload sanctioned plan',
        detail: 'Plan OCR and verification',
        route: '/enforcement/plans',
        show: this.session.canAccessPlans(),
      },
      {
        label: 'Register LiDAR scan',
        detail: 'Drone and vehicle LAS upload',
        route: '/enforcement/lidar-survey',
        show: this.session.canAccessScans(),
      },
      {
        label: 'Compare measurements',
        detail: 'Plan vs as-built check',
        route: '/enforcement/measurements',
        show: this.session.canAccessMeasurements(),
      },
      {
        label: 'Review violation flags',
        detail: 'Officer decision workflow',
        route: '/enforcement/flags',
        show: this.session.canAccessFlags(),
      },
      {
        label: 'View audit trail',
        detail: 'System activity log',
        route: '/enforcement/audit',
        show: this.session.canAccessFlags() || this.session.canAccessConfig(),
      },
    ].filter((a) => a.show),
  );

  readonly activeBuildingChecks = computed((): ActiveBuildingCheck[] => {
    const plotRefs = new Set<string>();
    for (const m of this.measurements()) {
      if (m.statusCd === 'final') plotRefs.add(m.plotRef);
    }
    for (const f of this.flags()) {
      if (f.statusCd !== 'superseded') plotRefs.add(f.plotRef);
    }
    for (const p of this.plans()) plotRefs.add(p.plotRef);

    const latestFlagForPlot = (plotRef: string): ViolationFlag | undefined => {
      return [...this.flags()]
        .filter((f) => f.plotRef === plotRef && f.statusCd !== 'superseded')
        .sort((a, b) => b.id - a.id)[0];
    };

    const rows: ActiveBuildingCheck[] = [...plotRefs].map((plotRef) => {
      const plan = this.plans().find((p) => p.plotRef === plotRef);
      const measurement =
        this.measurements().find((m) => m.plotRef === plotRef && m.statusCd === 'final') ||
        this.measurements().find((m) => m.plotRef === plotRef);
      const latestFlag = latestFlagForPlot(plotRef);
      const openFlag =
        latestFlag && ['open', 'under_review', 'recheck_requested'].includes(latestFlag.statusCd)
          ? latestFlag
          : undefined;

      const planPart = plan ? `Plan ${plan.planNo || 'on file'}` : 'No plan';
      const measPart = measurement
        ? `Measured (${measurement.statusCd || 'recorded'})`
        : 'Not measured';

      let ruleCheckLabel = 'Pending';
      let ruleCheckStatus = 'pending';
      let violationLabel = '';
      let violationStatus: string | null = null;
      let flagId: number | undefined;

      if (openFlag) {
        ruleCheckLabel = 'Completed';
        ruleCheckStatus = 'completed';
        violationStatus = openFlag.statusCd;
        violationLabel = 'Violation flagged';
        flagId = openFlag.id;
      } else if (latestFlag?.statusCd === 'rejected') {
        ruleCheckLabel = 'Completed';
        ruleCheckStatus = 'completed';
        violationStatus = 'rejected';
        violationLabel = 'Rejected by officer';
        flagId = latestFlag.id;
      } else if (latestFlag?.statusCd === 'accepted') {
        ruleCheckLabel = 'Completed';
        ruleCheckStatus = 'completed';
        violationStatus = 'accepted';
        violationLabel = 'Accepted — closed';
        flagId = latestFlag.id;
      } else if (latestFlag?.statusCd === 'recheck_requested') {
        ruleCheckLabel = 'Recheck pending';
        ruleCheckStatus = 'awaiting';
        violationStatus = 'recheck_requested';
        violationLabel = 'Recheck requested';
        flagId = latestFlag.id;
      } else if (measurement?.statusCd === 'final') {
        ruleCheckLabel = 'Awaiting check';
        ruleCheckStatus = 'awaiting';
      }

      return {
        plotRef,
        progressLabel: `${planPart} · ${measPart}`,
        ruleCheckLabel,
        ruleCheckStatus,
        violationLabel,
        violationStatus,
        planId: plan?.id,
        flagId,
        measurementPlotRef: measurement?.plotRef,
      };
    });

    return rows.sort((a, b) => a.plotRef.localeCompare(b.plotRef)).slice(0, 8);
  });

  constructor() {
    effect(() => {
      this.session.authorityId();
      this.session.role();
      untracked(() => this.reload());
    });
  }

  reload(): void {
    this.loading.set(true);
    this.error.set('');

    const empty = of({ data: [] as never[] });

    this.api
      .purgeSupersededHistory()
      .pipe(
        catchError(() => of(null)),
        switchMap(() =>
          forkJoin({
            plans: this.session.canAccessPlans()
              ? this.api.listPlans().pipe(catchError(() => empty))
              : empty,
            scans: this.session.canAccessScans()
              ? this.api.listScanJobs().pipe(catchError(() => empty))
              : empty,
            measurements: this.session.canAccessMeasurements()
              ? this.api.listMeasurements().pipe(catchError(() => empty))
              : empty,
            flags: this.session.canAccessFlags()
              ? this.api.listFlags().pipe(catchError(() => empty))
              : empty,
          }),
        ),
      )
      .subscribe({
        next: (res) => {
          this.plans.set(res.plans.data || []);
          this.scans.set(res.scans.data || []);
          this.measurements.set(res.measurements.data || []);
          this.flags.set(res.flags.data || []);
          this.loading.set(false);
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(err?.error?.message || 'Unable to load dashboard');
        },
      });
  }

  plotLabel(plotRef: string): string {
    const ref = String(plotRef || '').trim();
    const khasra = ref.replace(/^khasra-/i, '');
    return khasra ? `Plot ${khasra}` : ref || '—';
  }

  openDetails(row: ActiveBuildingCheck): void {
    if (row.measurementPlotRef && this.session.canAccessMeasurements()) {
      void this.router.navigate(['/enforcement/measurements'], {
        queryParams: { plotRef: row.measurementPlotRef, compare: '1' },
      });
      return;
    }
    if (row.planId && this.session.canAccessPlans()) {
      void this.router.navigate(['/enforcement/plans'], { queryParams: { planId: row.planId } });
      return;
    }
    void this.router.navigate(['/enforcement/measurements']);
  }

  openFlag(row: ActiveBuildingCheck): void {
    if (!row.flagId) return;
    void this.router.navigate(['/enforcement/flags'], { queryParams: { flagId: row.flagId } });
  }

  canReviewFlag(row: ActiveBuildingCheck): boolean {
    return (
      !!row.flagId &&
      ['open', 'under_review', 'recheck_requested'].includes(row.violationStatus || '')
    );
  }
}
